import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { isArray } from "lodash";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import ApexChart from "react-apexcharts";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { disconnectWebsocket } from "../../../store/actions/websocketActions";
import Analytics from "../../admin/Analytics";

const Chart = styled(ApexChart)``;

const DATE_PRESETS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "custom", label: "Custom" },
];

const GROUP_BY_OPTIONS = ["Day", "Week", "Month"];

const KPI_TEXTS = {
  initiated: [
    "Tracking",
    "sessions in range",
    "Tracking",
    "sessions in range",
    "Tracking",
    "sessions in range",
  ],
  completed: [
    "Completed",
    "sessions",
    "Completed",
    "sessions",
    "Completed",
    "sessions",
  ],
  completionRate: [
    "Completion rate is",
    "percent",
    "Completion rate is",
    "percent",
    "Completion rate is",
    "percent",
  ],
  ron: ["RON", "sessions", "RON", "sessions", "RON", "sessions"],
  ipen: ["IPEN", "sessions", "IPEN", "sessions", "IPEN", "sessions"],
  esign: ["eSign", "sessions", "eSign", "sessions", "eSign", "sessions"],
};

const useBarChartOptions = (theme, labels, horizontal = false) => ({
  chart: {
    background: "transparent",
    stacked: false,
    toolbar: { show: false },
  },
  colors: [theme.palette.primary.main, theme.palette.success.main],
  dataLabels: { enabled: false },
  fill: { opacity: 1, type: "solid" },
  grid: {
    borderColor: theme.palette.divider,
    strokeDashArray: 2,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  legend: { show: true },
  plotOptions: {
    bar: {
      horizontal,
      borderRadius: 4,
      columnWidth: horizontal ? undefined : "45%",
    },
  },
  stroke: {
    colors: ["transparent"],
    show: true,
    width: 2,
  },
  theme: { mode: theme.palette.mode },
  xaxis: {
    categories: labels,
    labels: {
      rotate: horizontal ? 0 : -45,
      rotateAlways: !horizontal,
      style: {
        colors: theme.palette.text.secondary,
        fontSize: "13px",
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: theme.palette.text.secondary,
        fontSize: "13px",
      },
      maxWidth: horizontal ? 80 : undefined,
    },
  },
  tooltip: {
    theme: theme.palette.mode,
  },
});

const useDonutOptions = (theme, labels) => ({
  chart: { background: "transparent" },
  colors: [
    theme.palette.primary.main,
    theme.palette.warning.main,
    theme.palette.info.main,
  ],
  dataLabels: { enabled: false },
  labels,
  legend: { position: "bottom" },
  stroke: { width: 0 },
  theme: { mode: theme.palette.mode },
  tooltip: { fillSeriesColor: false },
});

const formatUsDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getGridFormatterValue = (input) => {
  if (
    input !== null &&
    typeof input === "object" &&
    Object.prototype.hasOwnProperty.call(input, "value")
  ) {
    return input.value;
  }

  return input;
};

const SessionUsage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [viewDetail, setViewDetail] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [datePreset, setDatePreset] = useState("this_month");
  const [groupBy, setGroupBy] = useState("Day");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [errorText, setErrorText] = useState("");
  const [summaryData, setSummaryData] = useState({
    totals: {
      initiated: 0,
      completed: 0,
      completionRate: 0,
      ron: 0,
      ipen: 0,
      esign: 0,
    },
    trend: { labels: [], initiatedSeries: [], completedSeries: [] },
    typeDistribution: { labels: ["RON", "IPEN", "eSign"], series: [0, 0, 0] },
    topContractors: { labels: [], series: [] },
    bnSessions: {
      totals: {
        initiated: 0,
        completed: 0,
        completionRate: 0,
        ron: 0,
        ipen: 0,
        esign: 0,
      },
    },
  });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(tabletSize ? 5 : 10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([
    { field: "sessionDate", sort: "desc" },
  ]);

  useEffect(() => {
    if (tabletSize) {
      setRowsPerPage(5);
    } else {
      setRowsPerPage(10);
    }
  }, [tabletSize]);

  useEffect(() => {
    if (currentUser?.user) {
      if (currentUser?.user?.role !== "admin" || !currentUser?.user?.admin) {
        dispatch(disconnectWebsocket(currentSession));
        navigate("/login");
      }
    }
  }, [currentUser?.user, currentSession, dispatch, navigate]);

  const handleCloseViewDetail = () => {
    setViewDetail(false);
    setSelectedRow(null);
  };

  const buildPayload = useCallback(() => {
    const payload = {
      currentSession,
      currentPath: location.pathname,
      datePreset,
      groupBy,
    };

    if (datePreset === "custom") {
      payload.fromDate = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "";
      payload.toDate = toDate ? dayjs(toDate).format("YYYY-MM-DD") : "";
    }

    return payload;
  }, [
    currentSession,
    location.pathname,
    datePreset,
    groupBy,
    fromDate,
    toDate,
  ]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/session-usage-report/summary`,
        buildPayload(),
        { withCredentials: true },
      );

      if (resp.data.success) {
        setSummaryData(resp.data.data);
        setErrorText("");
      } else {
        setErrorText(
          resp.data.message || "Unable to load session usage summary.",
        );
      }
    } catch (error) {
      setErrorText("Unable to load session usage summary.");
    } finally {
      setSummaryLoading(false);
    }
  }, [buildPayload]);

  const fetchRows = useCallback(
    async (
      nextPage = page,
      nextRowsPerPage = rowsPerPage,
      nextSortModel = sortModel,
    ) => {
      setRowsLoading(true);
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/session-usage-report/rows`,
          {
            ...buildPayload(),
            page: nextPage,
            rowsPerPage: nextRowsPerPage,
            sortModel: nextSortModel,
          },
          { withCredentials: true },
        );

        if (resp.data.success) {
          setRows(resp.data.data.rows || []);
          setTotalCount(resp.data.data.totalCount || 0);
          setErrorText("");
        } else {
          setErrorText(
            resp.data.message || "Unable to load session usage rows.",
          );
        }
      } catch (error) {
        setErrorText("Unable to load session usage rows.");
      } finally {
        setRowsLoading(false);
      }
    },
    [buildPayload, page, rowsPerPage, sortModel],
  );

  useEffect(() => {
    fetchSummary();
    fetchRows(0, rowsPerPage, sortModel);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, groupBy, fromDate, toDate]);

  const trendSeries = useMemo(
    () => [
      { name: "Initiated", data: summaryData.trend.initiatedSeries || [] },
      { name: "Completed", data: summaryData.trend.completedSeries || [] },
    ],
    [summaryData],
  );

  const trendOptions = useBarChartOptions(
    theme,
    summaryData.trend.labels || [],
    false,
  );

  const topContractorSeries = useMemo(
    () => [{ name: "Sessions", data: summaryData.topContractors.series || [] }],
    [summaryData],
  );

  const topContractorOptions = useBarChartOptions(
    theme,
    summaryData.topContractors.labels || [],
    true,
  );

  const typeOptions = useDonutOptions(
    theme,
    summaryData.typeDistribution.labels || [],
  );

  const columns = useMemo(
    () => [
      {
        field: "notarizationId",
        headerName: "Notarization ID",
        minWidth: 190,
        flex: 1.1,
      },
      {
        field: "taskId",
        headerName: "Task ID",
        minWidth: 140,
        flex: 0.8,
      },
      { field: "sessionType", headerName: "Type", minWidth: 110, flex: 0.7 },
      {
        field: "sessionDate",
        headerName: "Session Date",
        minWidth: 180,
        flex: 1.1,
        valueFormatter: (params) =>
          formatUsDateTime(getGridFormatterValue(params)),
      },
      {
        field: "contractorName",
        headerName: "Contractor",
        minWidth: 220,
        flex: 1.3,
      },
      {
        field: "contractorLicense",
        headerName: "License",
        minWidth: 140,
        flex: 0.9,
      },
      {
        field: "workflowCompleted",
        headerName: "Billable Completed",
        minWidth: 150,
        flex: 0.8,
        valueFormatter: (params) =>
          getGridFormatterValue(params) ? "Yes" : "No",
      },
      {
        field: "bnIds",
        headerName: "All BN IDs",
        minWidth: 220,
        flex: 1.4,
      },
      {
        field: "completedBnIds",
        headerName: "Completed BN IDs",
        minWidth: 220,
        flex: 1.4,
      },
      {
        field: "incompleteBnIds",
        headerName: "Incomplete BN IDs",
        minWidth: 220,
        flex: 1.4,
      },
      {
        field: "numSigners",
        headerName: "# Signers",
        minWidth: 110,
        flex: 0.7,
      },
      {
        field: "numCompletedSigners",
        headerName: "# Completed",
        minWidth: 120,
        flex: 0.7,
      },
      {
        field: "signers",
        headerName: "Signers",
        minWidth: 240,
        flex: 1.4,
      },
      {
        field: "completionDate",
        headerName: "Completion Date",
        minWidth: 190,
        flex: 1.1,
        valueFormatter: (params) =>
          formatUsDateTime(getGridFormatterValue(params)),
      },
      { field: "county", headerName: "County", minWidth: 140, flex: 0.8 },
      {
        field: "projectAddress",
        headerName: "Project Address",
        minWidth: 260,
        flex: 1.8,
      },
    ],
    [],
  );

  const columnsSmall = [
    {
      field: "notarizationId",
      headerName: "Session usage",
      flex: 1,
      editable: false,
      renderCell: (params) => {
        return (
          <>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                pr: 2,
                flexDirection: mobileSize ? "column" : "row",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  pb: 0.2,
                  pl: mobileSize ? 0 : 1,
                  minWidth: "194px",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "rgba(0, 0, 0, 0.8)",
                    fontWeight: 500,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {params?.row?.notarizationId}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  {params?.row?.sessionType} • {params?.row?.contractorLicense}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "rgba(0, 0, 0, 0.54)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {params?.row?.contractorName}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  pb: 0.2,
                  pr: mobileSize ? 0 : 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  {formatUsDateTime(
                    getGridFormatterValue({
                      value: params?.row?.sessionDate,
                    }),
                  )}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "#0000FF",
                textDecoration: "underline",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                pl: mobileSize ? 0 : 1,
                cursor: "pointer",
              }}
              onClick={() => {
                setViewDetail(true);
                setSelectedRow(params?.row);
              }}
            >
              Click here to see the full detail
            </Typography>
          </>
        );
      },
    },
  ];

  const viewData = useMemo(() => {
    return [
      { label: "Notarization ID", value: selectedRow?.notarizationId || "N/A" },
      { label: "Task ID", value: selectedRow?.taskId || "N/A" },
      [
        { label: "Type", value: selectedRow?.sessionType || "N/A" },
        {
          label: "Session Date",
          value: formatUsDateTime(
            getGridFormatterValue({ value: selectedRow?.sessionDate }),
          ),
        },
      ],
      { label: "Contractor", value: selectedRow?.contractorName || "N/A" },
      [
        { label: "License", value: selectedRow?.contractorLicense || "N/A" },
        {
          label: "Billable Completed",
          value: getGridFormatterValue({
            value: selectedRow?.workflowCompleted,
          })
            ? "Yes"
            : "No",
        },
      ],

      { label: "All BN IDs", value: selectedRow?.bnIds || "N/A" },
      {
        label: "Completed BN IDs",
        value: selectedRow?.completedBnIds || "N/A",
      },
      {
        label: "Incomplete BN IDs",
        value: selectedRow?.incompleteBnIds || "N/A",
      },
      [
        { label: "# Signers", value: selectedRow?.numSigners || "N/A" },
        {
          label: "# Completed",
          value: selectedRow?.numCompletedSigners || "N/A",
        },
      ],
      { label: "Signers", value: selectedRow?.signers || "N/A" },
      [
        {
          label: "Completion Date",
          value: formatUsDateTime(
            getGridFormatterValue({ value: selectedRow?.completionDate }),
          ),
        },
        { label: "County", value: selectedRow?.county || "N/A" },
      ],
      { label: "Project Address", value: selectedRow?.projectAddress || "N/A" },
    ];
  }, [selectedRow]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
    fetchRows(model.page, model.pageSize, sortModel);
  };

  const handleSortModelChange = (model) => {
    const nextSortModel = model?.length ? model : [];
    setSortModel(nextSortModel);
    fetchRows(page, rowsPerPage, nextSortModel);
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Session Usage
            </Typography>
            <Typography color="text.secondary">
              Review initiated versus completed notarization and signing
              sessions across RON, IPEN, and eSign workflows.
            </Typography>
          </Box>

          {errorText ? <Alert severity="error">{errorText}</Alert> : null}

          <Alert severity="info">
            <strong>Billable workflows</strong> are grouped by notarization
            workflow (`notarization_id`). <strong>BN sessions</strong> are
            signer-level BlueNotary sessions (`bn_id`). A single billable
            workflow can contain multiple BN sessions, so these counts are
            expected to differ.
          </Alert>

          <Card>
            <CardHeader
              title="Filters"
              sx={{ padding: mobileSize ? "12px !important" : undefined }}
            />
            <CardContent
              sx={{ padding: mobileSize ? "12px !important" : undefined }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={datePreset}
                      label="Date Range"
                      onChange={(event) => setDatePreset(event.target.value)}
                    >
                      {DATE_PRESETS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Group By</InputLabel>
                    <Select
                      value={groupBy}
                      label="Group By"
                      onChange={(event) => setGroupBy(event.target.value)}
                    >
                      {GROUP_BY_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <DatePicker
                    disabled={datePreset !== "custom"}
                    label="From"
                    value={fromDate}
                    onChange={(value) => setFromDate(value)}
                    sx={{ width: "100%" }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <DatePicker
                    disabled={datePreset !== "custom"}
                    label="To"
                    value={toDate}
                    onChange={(value) => setToDate(value)}
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {summaryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="Initiated Billable Sessions"
                    count={summaryData.totals.initiated}
                    extra={summaryData.totals.initiated}
                    isMore={0}
                    texts={KPI_TEXTS.initiated}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="Completed Billable Sessions"
                    count={summaryData.totals.completed}
                    extra={summaryData.totals.completed}
                    isMore={0}
                    texts={KPI_TEXTS.completed}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="Completion Rate"
                    count={`${summaryData.totals.completionRate}%`}
                    extra={summaryData.totals.completionRate}
                    isMore={0}
                    texts={KPI_TEXTS.completionRate}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="RON Sessions"
                    count={summaryData.totals.ron}
                    extra={summaryData.totals.ron}
                    isMore={0}
                    texts={KPI_TEXTS.ron}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="IPEN Sessions"
                    count={summaryData.totals.ipen}
                    extra={summaryData.totals.ipen}
                    isMore={0}
                    texts={KPI_TEXTS.ipen}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="eSign Sessions"
                    count={summaryData.totals.esign}
                    extra={summaryData.totals.esign}
                    isMore={0}
                    texts={KPI_TEXTS.esign}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  BN Signer Sessions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These counts are based on signer-level BN sessions (`bn_id`),
                  which match the detailed grid rows below.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="Initiated BN Sessions"
                    count={summaryData.bnSessions?.totals?.initiated || 0}
                    extra={summaryData.bnSessions?.totals?.initiated || 0}
                    isMore={0}
                    texts={KPI_TEXTS.initiated}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="Completed BN Sessions"
                    count={summaryData.bnSessions?.totals?.completed || 0}
                    extra={summaryData.bnSessions?.totals?.completed || 0}
                    isMore={0}
                    texts={KPI_TEXTS.completed}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="BN Completion Rate"
                    count={`${summaryData.bnSessions?.totals?.completionRate || 0}%`}
                    extra={summaryData.bnSessions?.totals?.completionRate || 0}
                    isMore={0}
                    texts={KPI_TEXTS.completionRate}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="BN RON Sessions"
                    count={summaryData.bnSessions?.totals?.ron || 0}
                    extra={summaryData.bnSessions?.totals?.ron || 0}
                    isMore={0}
                    texts={KPI_TEXTS.ron}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="BN IPEN Sessions"
                    count={summaryData.bnSessions?.totals?.ipen || 0}
                    extra={summaryData.bnSessions?.totals?.ipen || 0}
                    isMore={0}
                    texts={KPI_TEXTS.ipen}
                  />
                </Grid>
                <Grid size={{ xs: 12, xsm: 6, md: 4, lg: 3, xl: 2 }}>
                  <Analytics
                    title="BN eSign Sessions"
                    count={summaryData.bnSessions?.totals?.esign || 0}
                    extra={summaryData.bnSessions?.totals?.esign || 0}
                    isMore={0}
                    texts={KPI_TEXTS.esign}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Billable Workflow Charts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The charts below remain grouped by billable notarization
                workflows (`notarization_id`), not signer BN sessions.
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 8 }}>
                  <Card>
                    <CardHeader
                      title="Initiated vs Completed Over Time"
                      sx={{
                        padding: mobileSize ? "12px !important" : undefined,
                      }}
                    />
                    <CardContent
                      sx={{
                        padding: mobileSize ? "12px !important" : undefined,
                      }}
                    >
                      <Chart
                        type="bar"
                        height={350}
                        options={trendOptions}
                        series={trendSeries}
                        width="100%"
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                  <Card>
                    <CardHeader
                      title="Session Type Distribution"
                      sx={{
                        padding: mobileSize ? "12px !important" : undefined,
                      }}
                    />
                    <CardContent
                      sx={{
                        padding: mobileSize ? "12px !important" : undefined,
                      }}
                    >
                      <Chart
                        type="donut"
                        height={350}
                        options={typeOptions}
                        series={summaryData.typeDistribution.series || []}
                        width="100%"
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Card>
                    <CardHeader
                      title="Top Contractors by Session Count"
                      sx={{
                        padding: mobileSize ? "12px !important" : undefined,
                      }}
                    />
                    <CardContent
                      sx={{
                        padding: mobileSize ? "12px !important" : undefined,
                      }}
                    >
                      <Chart
                        type="bar"
                        height={350}
                        options={topContractorOptions}
                        series={topContractorSeries}
                        width="100%"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

          <Card>
            <CardHeader
              title="Detailed Session Usage"
              sx={{ padding: mobileSize ? "12px !important" : undefined }}
            />
            <CardContent
              sx={{ padding: mobileSize ? "12px !important" : undefined }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The grid below is now grouped to{" "}
                <strong>one row per permit package workflow</strong>{" "}
                (`notarization_id`). It shows whether the billable workflow is
                completed/finalized and which BN session IDs are completed
                versus incomplete.
              </Typography>
              <DataGrid
                rows={rows}
                columns={tabletSize ? columnsSmall : columns}
                rowCount={totalCount}
                loading={rowsLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                rowHeight={mobileSize ? 102 : tabletSize ? 84 : 40}
                paginationMode="server"
                sortingMode="server"
                disableRowSelectionOnClick
                paginationModel={{ page, pageSize: rowsPerPage }}
                sortModel={sortModel}
                onPaginationModelChange={handlePaginationModelChange}
                onSortModelChange={handleSortModelChange}
                sx={{
                  minHeight: 500,
                  "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader:focus-within": {
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    padding: 0,
                  },
                  border: "1px solid #FCF2F0",
                  "& .MuiDataGrid-cell": {
                    borderColor: "#333333",
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    display: "none",
                  },
                }}
              />
            </CardContent>
          </Card>
        </Stack>
      </Container>
      <Dialog
        open={viewDetail}
        onClose={handleCloseViewDetail}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { ...(mobileSize ? { margin: 0, width: "95%" } : {}) },
          },
        }}
      >
        <DialogTitle id="confirm-dialog-title">View session usage</DialogTitle>
        <DialogContent>
          <Table>
            <TableBody>
              {(mobileSize ? viewData?.flat() : viewData)?.map((row, index) => (
                <TableRow key={index}>
                  {mobileSize ? (
                    <TableCell component="th" scope="row">
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "inherit",
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          fontSize: "inherit",
                        }}
                      >
                        {row.value}
                      </Typography>
                    </TableCell>
                  ) : (
                    <>
                      {isArray(row) && row?.length ? (
                        <TableCell colSpan={2}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            {row?.map((item) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "inherit",
                                  }}
                                >
                                  {item.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                    fontSize: "inherit",
                                  }}
                                >
                                  {item.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ fontWeight: "bold" }}
                          >
                            {row.label}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                            }}
                          >
                            {row.value}
                          </TableCell>
                        </>
                      )}
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseViewDetail}
            color="primary"
            variant="contained"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionUsage;
